import React from 'react'
import { useNavigationStore } from '@/stores/navigation'

interface PlatformPrivacyPolicyModalProps {
  isOpen: boolean
  onClose: () => void
}

const PlatformPrivacyPolicyModal: React.FC<PlatformPrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  const navigationStore = useNavigationStore()

  const handleWebsitePrivacyClick = () => {
    onClose() // Close current modal
    navigationStore.openPrivacyPolicyModal() // Open website privacy policy modal
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Eceptionist Platform Privacy Policy and Data Privacy Framework (DPF) Statement</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Divider */}
          <div className="border-b border-blue-200 mb-6"></div>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-600 mb-6">
              <strong>Effective Date:</strong> September 22, 2025
            </p>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">1. Scope</h3>
              <p className="text-gray-700 mb-4">
                This Platform Privacy Policy applies to personal data processed through the EceptionistCX SaaS platform and related services. It does not apply to visitors of our corporate website, which is governed separately by our 
                <button 
                  onClick={handleWebsitePrivacyClick}
                  className="bg-yellow-200 px-1 rounded hover:bg-yellow-300 transition-colors cursor-pointer underline"
                >
                  Website Privacy Policy
                </button>.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">2. Introduction and DPF Commitment</h3>
              <p className="text-gray-700 mb-4">
                E-Ceptionist, Inc. ("Eceptionist") is committed to protecting the privacy of our customers and end users. Eceptionist complies with the EU-U.S. Data Privacy Framework (EU-U.S. DPF), the UK Extension to the EU-U.S. DPF, and the Swiss-U.S. Data Privacy Framework (Swiss-U.S. DPF) as set forth by the U.S. Department of Commerce. Eceptionist has certified to the U.S. Department of Commerce that it adheres to the EU-U.S. DPF Principles, the UK Extension, and the Swiss-U.S. DPF Principles with respect to the processing of personal data transferred from the European Union, the United Kingdom, and Switzerland. If there is any conflict between this Privacy Policy and the DPF Principles, the Principles shall govern. To learn more about the DPF program and to view our certification, please visit <a href="https://www.dataprivacyframework.gov/" className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">https://www.dataprivacyframework.gov/</a>.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">3. Types of Personal Data Collected and Purpose of Use</h3>
              <p className="text-gray-700 mb-4">
                Through the EceptionistCX platform, we collect and process categories of data including patient demographic data, appointment data, referral and service request data, case management data, and other related healthcare information. This data is processed for the purpose of delivering and supporting workflows such as scheduling, referrals, case management, and related healthcare use cases.
              </p>
              <p className="text-gray-700 mb-4">
                Legal bases for processing under the GDPR/UK GDPR include: (i) consent, (ii) contractual necessity, (iii) compliance with legal obligations, (iv) protection of vital interests, (v) performance of tasks carried out in the public interest, and (vi) legitimate interests pursued by Eceptionist or its customers.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">4. Data Sharing and Transfers to Third Parties</h3>
              <p className="text-gray-700 mb-4">
                We may share personal data with healthcare facilities, service providers, and other organizations involved in providing services to patients or customers. In cases of onward transfer to third parties, Eceptionist remains liable under the DPF Principles unless we demonstrate that we are not responsible for the event giving rise to the damage.
              </p>
              <p className="text-gray-700 mb-4">
                In addition to compliance with the DPF, we also respect applicable international frameworks such as Canada's PIPEDA and Brazil's LGPD.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">5. Data Subject Rights</h3>
              <p className="text-gray-700 mb-4">Individuals whose data is processed in the EceptionistCX platform may exercise the following rights, subject to applicable law:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Right to access, correct, or delete personal data</li>
                <li>Right to restrict or object to processing</li>
                <li>Right to data portability</li>
                <li>Right to withdraw consent where consent is the legal basis for processing</li>
                <li>Right to object to automated decision-making and profiling</li>
                <li>Right to lodge a complaint with a supervisory authority (EEA/UK/Switzerland)</li>
                <li>Right to opt out of certain processing: If we use your personal data for a purpose materially different from the purpose for which it was originally collected or subsequently authorized, you will have the opportunity to opt out of such processing, unless required to deliver the services you requested or as otherwise permitted by law.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">6. California Privacy Rights</h3>
              <p className="text-gray-700 mb-4">
                If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act (CPRA). These include the right to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Know what categories of personal information we collect, use, or disclose</li>
                <li>Request deletion of personal information (subject to certain exceptions)</li>
                <li>Opt out of the sale or sharing of personal information (note: we do not sell personal information)</li>
                <li>Be free from discrimination for exercising your privacy rights</li>
              </ul>
              <p className="text-gray-700 mb-4">
                To exercise your rights, please contact us at <a href="mailto:management@eceptionist.com" className="text-blue-600 hover:text-blue-800 underline">management@eceptionist.com</a>. You may designate an authorized agent to make a request on your behalf.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">7. Dispute Resolution and Independent Recourse Mechanism</h3>
              <p className="text-gray-700 mb-4">
                In compliance with the DPF Principles, Eceptionist commits to resolving complaints about our collection or use of your personal data. Individuals with inquiries or complaints should first contact us at <a href="mailto:management@eceptionist.com" className="text-blue-600 hover:text-blue-800 underline">management@eceptionist.com</a>. For unresolved complaints, we have engaged JAMS, a third-party recourse mechanism based in the United States. Further, Eceptionist commits to cooperate with EU data protection authorities (DPAs), the UK Information Commissioner's Office (ICO), and the Swiss Federal Data Protection and Information Commissioner (FDPIC) regarding unresolved complaints.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">8. Data Security</h3>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational measures to protect personal data against unauthorized access, disclosure, alteration, or destruction. These measures include encryption of data in transit and at rest, role-based access controls, secure hosting and regular security assessments.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">9. Data Retention</h3>
              <p className="text-gray-700 mb-4">
                We retain personal data for the duration of the customer contract and for a limited period thereafter, typically up to 12 months, unless a longer retention period is required by law, regulation, or contract. This allows us to support customer transition, address legal obligations, and resolve disputes.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">10. Accountability and Enforcement</h3>
              <p className="text-gray-700 mb-4">
                Eceptionist is subject to the investigatory and enforcement powers of the U.S. Federal Trade Commission (FTC). We may be required to disclose personal data in response to lawful requests by public authorities, including to meet national security or law enforcement requirements.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">11. Changes to This Policy</h3>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors. When we make changes, we will update the Effective Date and provide additional notice as required.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">12. Contact Us</h3>
              <div className="text-gray-700">
                <p className="mb-2"><strong>Eceptionist</strong></p>
                <p className="mb-2">Email: <a href="mailto:management@eceptionist.com" className="text-blue-600 hover:text-blue-800 underline">management@eceptionist.com</a></p>
                <p className="mb-2">Address: 7000 N. Mopac Expressway, Suite 200 – 1312, Austin, Texas 78731</p>
                <p className="mb-2">Phone: <a href="tel:713-520-6688" className="text-blue-600 hover:text-blue-800 underline">713-520-6688</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlatformPrivacyPolicyModal
